/**
 * MemoRenderer — minimal Markdown-to-JSX renderer used by the
 * AI Compliance Memo modal and Sample Memo page.
 *
 * Supports: ## headings, ### sub-headings, **bold**, *italic*,
 * --- horizontal rules, "- " / "* " unordered list items, and paragraphs.
 * Intentionally tiny — no external dependency.
 */

const inlineFormat = (text) => {
  // Escape HTML special chars first
  let s = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  // Bold: **text**
  s = s.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-foreground">$1</strong>');
  // Italic: *text* (avoid touching bold which we just replaced)
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  // Inline code: `code`
  s = s.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-muted text-xs">$1</code>');
  return s;
};

const MemoRenderer = ({ markdown = "", className = "", ...rest }) => {
  if (!markdown) return null;

  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let buffer = [];
  let listBuffer = [];

  const flushParagraph = () => {
    if (buffer.length) {
      blocks.push({ type: "p", text: buffer.join(" ").trim() });
      buffer = [];
    }
  };
  const flushList = () => {
    if (listBuffer.length) {
      blocks.push({ type: "ul", items: [...listBuffer] });
      listBuffer = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushParagraph();
      flushList();
      continue;
    }
    if (/^---+$/.test(line.trim())) {
      flushParagraph();
      flushList();
      blocks.push({ type: "hr" });
      continue;
    }
    if (line.startsWith("### ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h3", text: line.slice(4) });
      continue;
    }
    if (line.startsWith("## ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h2", text: line.slice(3) });
      continue;
    }
    if (line.startsWith("# ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h1", text: line.slice(2) });
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      flushParagraph();
      listBuffer.push(line.replace(/^\s*[-*]\s+/, ""));
      continue;
    }
    flushList();
    buffer.push(line);
  }
  flushParagraph();
  flushList();

  return (
    <div className={`memo-renderer ${className}`} {...rest}>
      {blocks.map((b, i) => {
        if (b.type === "hr") {
          return <hr key={i} className="my-6 border-border" />;
        }
        if (b.type === "h1") {
          return (
            <h1
              key={i}
              className="font-display font-black text-2xl text-foreground mt-6 mb-3"
              dangerouslySetInnerHTML={{ __html: inlineFormat(b.text) }}
            />
          );
        }
        if (b.type === "h2") {
          return (
            <h2
              key={i}
              className="font-display font-bold text-lg text-foreground mt-6 mb-2 pb-1.5 border-b border-border"
              dangerouslySetInnerHTML={{ __html: inlineFormat(b.text) }}
            />
          );
        }
        if (b.type === "h3") {
          return (
            <h3
              key={i}
              className="font-display font-bold text-base text-foreground mt-5 mb-2"
              dangerouslySetInnerHTML={{ __html: inlineFormat(b.text) }}
            />
          );
        }
        if (b.type === "ul") {
          return (
            <ul key={i} className="my-3 space-y-1.5 list-disc list-outside ml-5 marker:text-accent">
              {b.items.map((it, j) => (
                <li
                  key={j}
                  className="text-foreground/90"
                  dangerouslySetInnerHTML={{ __html: inlineFormat(it) }}
                />
              ))}
            </ul>
          );
        }
        return (
          <p
            key={i}
            className="my-3 text-foreground/90 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: inlineFormat(b.text) }}
          />
        );
      })}
    </div>
  );
};

export default MemoRenderer;
